import { CoreNetworkManager } from '@bugmail-js/core/network-manager';

/**
 * Network Manager
 * Handles API communication with retry logic and offline storage
 */

class NetworkManager extends CoreNetworkManager {
  constructor(config) {
    super(config);
    this.retryQueue = [];
    this.maxRetries = 3;
    this.retryTimeout = 5000; // 5 seconds
    this.offlineStorage = this.initializeOfflineStorage();
    this.setupOnlineListener();
  }

  /**
   * Initialize offline storage for errors
   * @private
   */
  initializeOfflineStorage() {
    try {
      const storage = window?.localStorage?.getItem('bugmail_offline_errors');
      return storage ? JSON.parse(storage) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Set up online/offline event listeners
   * @private
   */
  setupOnlineListener() {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      this.processOfflineErrors();
    });
  }

  /**
   * Send error report to BugMail API
   * @param {Object} errorReport - Error report to send
   * @returns {Promise<Object>} - API response or status object
   */
  async sendError(errorReport) {
    if (!this.config.apiKey) {
      console.error('BugMail: No API key provided');
      return;
    }

    // Add API key to report (already done in bugmail-client now, but keep for safety/offline)
    const report = {
      ...errorReport,
      api_key: this.config.apiKey, 
      // sdk_version is now added in bugmail-client before calling sendError
    };

    try {
      // Check if online
      if (!window.navigator.onLine) {
        this.storeErrorOffline(report);
        return { success: false, status: 'stored_offline', message: 'Browser offline' };
      }

      // *** Use the new SDK-specific endpoint path ***
      const response = await this.makeRequest('/api/sdk/v1/errors', { 
        method: 'POST',
        body: JSON.stringify(report) // Send the augmented report
      });

      // If makeRequest succeeds, return the actual response
      return { success: true, status: 'sent', data: response };

    } catch (error) {
      // Handle different error scenarios
      if (this.isNetworkError(error)) {
        this.storeErrorOffline(report);
        return { success: false, status: 'stored_offline', message: 'Network error, stored offline', error: error };
      }

      // Check if it's an HTTP error we decided not to retry (like 4xx)
      if (error && typeof error.status === 'number' && !this.shouldRetry(error)) {
         console.error(`[BugMail Network] Request failed with status ${error.status}. Not retrying.`, error.body || error.message);
         return { success: false, status: `failed_${error.status}`, message: `Request failed with status ${error.status}`, error: error };
      }

      // If it's some other error after retries, or shouldRetry was true but failed max times
      console.error(`[BugMail Network] Failed to send error after retries or due to non-retriable error:`, error);
      return { success: false, status: 'failed_sending', message: 'Failed to send error after retries or due to non-retriable issue', error: error };
    }
  }

  /**
   * Make HTTP request with retry logic
   * @param {string} path - API path
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>} - API response
   * @private
   */
  async makeRequest(path, options) {
    // Ensure the base endpoint URL does not have a trailing slash if the path starts with one
    const baseEndpoint = this.config.endpoint.endsWith('/') 
        ? this.config.endpoint.slice(0, -1) 
        : this.config.endpoint;
    const url = `${baseEndpoint}${path}`;
    let attempt = 0;

    while (attempt < this.maxRetries) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'X-BugMail-API-Key': this.config.apiKey, // Keep sending API key header
            // SDK version header is added in bugmail-client before calling sendError
            // but could be added here too if needed consistently
            ...options.headers 
          }
        });

        if (!response.ok) {
          // Create an error object that includes the status
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status; // Attach status to the error object
          try {
            // Try to get response body for more context, but don't fail if it's not JSON
            error.body = await response.json(); 
          } catch (e) {
             error.body = await response.text(); // Fallback to text
          }
          throw error; // Throw the augmented error
        }

        return await response.json();
      } catch (error) {
        attempt++;
        
        // Use the updated shouldRetry logic
        if (attempt >= this.maxRetries || !this.shouldRetry(error)) {
          if (this.config.debug) {
            console.warn(`[BugMail Network] Request failed after ${attempt} attempts or won't retry:`, error);
          }
          throw error; // Re-throw the error if max retries reached or shouldn't retry
        }

        if (this.config.debug) {
           console.log(`[BugMail Network] Retrying request (attempt ${attempt}/${this.maxRetries}). Error:`, error);
        }
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, this.retryTimeout * attempt));
      }
    }
    // Should not be reached if loop finishes, but throw error just in case
    throw new Error('Max retries exceeded without success or failure determination.');
  }

  /**
   * Store error report for offline handling
   * @param {Object} errorReport - Error report to store
   * @private
   */
  storeErrorOffline(errorReport) {
    try {
      const errors = this.offlineStorage;
      errors.push(errorReport);
      window.localStorage.setItem('bugmail_offline_errors', JSON.stringify(errors));
      
      if (this.config.debug) {
        console.log('BugMail: Stored error offline for later sending');
      }
    } catch (e) {
      console.error('BugMail: Failed to store error offline', e);
    }
  }

  /**
   * Process stored offline errors when back online
   * @private
   */
  async processOfflineErrors() {
    const errors = this.offlineStorage;
    if (!errors.length) return;

    // Clear storage first to prevent duplicates
    window.localStorage.removeItem('bugmail_offline_errors');
    this.offlineStorage = [];

    // Try to send each stored error
    for (const error of errors) {
      try {
        await this.sendError(error);
      } catch (e) {
        console.error('BugMail: Failed to send offline error', e);
      }
    }
  }

  /**
   * Add error report to retry queue
   * @param {Object} errorReport - Error report to retry
   * @private
   */
  addToRetryQueue(errorReport) {
    this.retryQueue.push({
      report: errorReport,
      attempts: 0,
      nextRetry: Date.now() + this.retryTimeout
    });

    // Start retry process if not already running
    this.processRetryQueue();
  }

  /**
   * Process retry queue
   * @private
   */
  async processRetryQueue() {
    if (!this.retryQueue.length) return;

    const now = Date.now();
    const item = this.retryQueue[0];

    if (now < item.nextRetry) {
      setTimeout(() => this.processRetryQueue(), item.nextRetry - now);
      return;
    }

    try {
      await this.sendError(item.report);
      this.retryQueue.shift(); // Remove on success
    } catch (e) {
      item.attempts++;
      
      if (item.attempts >= this.maxRetries) {
        this.retryQueue.shift(); // Remove after max retries
        this.storeErrorOffline(item.report); // Store for later
      } else {
        item.nextRetry = Date.now() + (this.retryTimeout * item.attempts);
      }
    }

    // Continue processing queue
    if (this.retryQueue.length) {
      this.processRetryQueue();
    }
  }

  /**
   * Check if error is a network error
   * @param {Error} error - Error to check
   * @returns {boolean} - True if network error
   * @private
   */
  isNetworkError(error) {
    return (
      error.name === 'TypeError' &&
      (error.message.includes('Failed to fetch') ||
       error.message.includes('NetworkError') ||
       error.message.includes('Network request failed'))
    );
  }

  /**
   * Determine if request should be retried
   * @param {Error} error - Error to check. Assumes error object might have a 'status' property for HTTP errors.
   * @returns {boolean} - True if should retry
   * @private
   */
  shouldRetry(error) {
    // Check if it's a network error first
    if (this.isNetworkError(error)) {
      return true;
    }
    // Check if it's an HTTP error with a status code
    if (error && typeof error.status === 'number') {
      // Only retry on 5xx server errors
      return error.status >= 500 && error.status < 600;
    }
    // Do not retry other types of errors by default
    return false;
  }
}

export default NetworkManager;
